// MEMO undo/redo
// 状態のスナップショットを取る方式は楽だけど記録数が多いとDeepCopyのコストがでかいのと全再描画が走るのでやめたほうがいいと外人さんが言ってた
// 代わりにUndoに相当する操作を関数として記録して再実行する方式の方がいいっぽいけどundo相当の操作を登録側で指示する必要があって面倒くさそう
// とりあえず後者で進めてみる

import { reactive } from "vue";

// 操作記録レコード
interface Operation {
  name: string;
  doFunc: () => void;
  undoFunc: () => void;
  tag: unknown;
  beforeActiveElement: HTMLElement | undefined;
  afterActiveElement: HTMLElement | undefined;
}
// Undoのキー判定
export const isUndo = (e: KeyboardEvent) => {
  return (
    e.key == "z" && ((e.ctrlKey && !e.shiftKey) || (e.metaKey && !e.shiftKey))
  );
};
// Redoのキー判定
export const isRedo = (e: KeyboardEvent) => {
  return (
    (e.key == "Z" &&
      ((e.ctrlKey && e.shiftKey) || (e.metaKey && e.shiftKey))) ||
    (e.key == "z" &&
      ((e.ctrlKey && e.shiftKey) || (e.metaKey && e.shiftKey))) ||
    (e.key == "y" && e.ctrlKey)
  );
};
// HTMLElementかどうかを判定したい
const hasFocus = (elm: any) => elm.focus !== undefined;

// UndoRedoモジュール
export const useUndoRedo = () => {
  const state = reactive<{ list: Operation[]; index: number }>({
    // 操作記録リスト
    list: [],
    // 現在位置（Redoがなければ不要）
    index: -1,
  });
  // 操作の登録
  const add = (
    name: string,
    doFunc: () => void,
    undoFunc: () => void,
    tag?: unknown
  ) => {
    // 操作の実行関数とそのUndo相当の処理を受け取り記録する
    let before = undefined as HTMLElement | undefined;
    let after = undefined as HTMLElement | undefined;
    if (hasFocus(document.activeElement)) {
      before = document.activeElement as HTMLElement;
    }
    doFunc();
    if (hasFocus(document.activeElement)) {
      after = document.activeElement as HTMLElement;
    }

    // 記録の前に今の位置から先のRedo履歴を削除
    state.list.splice(state.index + 1, state.list.length - state.index + 1);

    // 記録
    state.list.push({
      name: name,
      tag: tag,
      doFunc: doFunc,
      undoFunc: undoFunc,
      beforeActiveElement: before,
      afterActiveElement: after,
    });
    state.index++;
    console.log(name);
  };

  const undo = () => {
    // 現在位置がなければ何もしない
    if (state.index < 0) return;
    // 今の位置の操作を取得
    const ope = state.list[state.index];
    if (ope === undefined) return;
    // 戻す前に当時のフォーカスを再現
    ope.afterActiveElement?.focus();
    // 戻す操作を実行
    ope.undoFunc();
    // 戻した後に当時のフォーカスを再現
    ope.beforeActiveElement?.focus();
    // 位置を一つ前に移動
    state.index--;
    console.log(`undo ${ope.name}`);
  };
  const redo = () => {
    // 進む位置がなければ何もしない
    const nextIndex = state.index + 1;
    if (state.list.length <= nextIndex) return;
    // １つ先の操作を取得
    const ope = state.list[nextIndex];
    if (ope === undefined) return;
    // 進む前に当時のフォーカスを再現
    ope.beforeActiveElement?.focus();
    // 進む操作を実行
    ope.doFunc();
    // 進んだ後に当時のフォーカスを再現
    ope.afterActiveElement?.focus();
    // 位置を一つ前に移動
    state.index = nextIndex;
    console.log(`redo ${ope.name}`);
  };

  // キーボード操作でundo,redoを操作できるようにする
  const keyHook = (e: KeyboardEvent) => {
    if (isUndo(e)) {
      undo();
      e.preventDefault();
    } else if (isRedo(e)) {
      redo();
      e.preventDefault();
    }
  };

  // Undoボタンを押せないようにする
  const disUndo = () => {
    //条件:配列の要素数が0時とこれ以上戻れない時(初期表示時と、undoしきったとき)
    if (state.list.length == 0 || state.index < 0) {
      return true;
    } else {
      return false;
    }
  };

  // Redoボタンを押せないようにする
  const disRedo = () => {
    //条件:配列の要素数が0時とこれ以上進めない時(初期表示時と、redoしきったとき)
    if (state.list.length == 0 || state.index == state.list.length - 1) {
      return true;
    } else {
      return false;
    }
  };

  // undo履歴から削除オブジェクトに関する履歴をクリア
  const clear = (predicate: (name: string, tag: unknown) => boolean) => {
    const tmp = state.list.map((x) => x);
    tmp.forEach((ope) => {
      // 削除対象じゃなければ何もしない
      // 削除対象だったら削除してindexを更新
      // indexの更新は以下のルール
      // index位置以下のitemを削除した時:-1
      // indexより大きいitemを削除した時:維持
      const isRemove = predicate(ope.name, ope.tag);
      if (!isRemove) {
        return;
      }

      const index = state.list.indexOf(ope);
      if (0 <= index) {
        state.list.splice(index, 1);
        if (index <= state.index) {
          state.index--;
        }
      }
    });
  };
  return {
    add,
    undo,
    redo,
    keyHook,
    disUndo,
    disRedo,
    clear,
  };
};

export type UndoRedo = ReturnType<typeof useUndoRedo>;
